import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ArcElement
} from 'chart.js';
import { Line, Bar, Doughnut } from 'react-chartjs-2';
import './RentalDashboard.css';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ArcElement
);

interface FleetSummary {
  totalUnits: number;
  activeUnits: number;
  inactiveUnits: number;
  avgUtilization: number;
  prevYearAvgUtilization: number | null;
}

interface RegionFleet {
  region: string;
  unitCount: number;
}

interface FacilityUtilization {
  facilityCode: string;
  facilityName: string;
  utilization: number;
}

interface UtilizationTrend {
  year: number;
  month: number;
  utilization: number;
}

interface EquipmentTypeUtilization {
  equipmentType: string;
  utilization: number;
}

interface Performer {
  name: string;
  utilization: number;
}

interface DashboardData {
  selectedYear: number;
  previousYear: number;
  latestMonth: number;
  fleetSummary: FleetSummary;
  sparklines: {
    utilization: number[];
    activeUnits: number[] | null;
  };
  performers: {
    top: Performer[];
    bottom: Performer[];
  };
  fleetByRegion: RegionFleet[];
  utilizationTrend: Record<string, UtilizationTrend[]>;
  currentUtilization: FacilityUtilization[];
  utilizationByType: Record<string, EquipmentTypeUtilization[]>;
  regionFacilities: Record<string, Array<{ facilityCode: string; facilityName: string }>>;
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

// Color palette
const COLORS = {
  primary: '#3b82f6',
  secondary: '#8b5cf6',
  success: '#10b981',
  warning: '#f59e0b',
  danger: '#ef4444',
  info: '#06b6d4',
  regions: ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#84cc16']
};

type ChartType = 'trend' | 'fleet' | 'facility' | 'equipment' | 'status';

// ============================================================================
// ANIMATED COUNTER COMPONENT
// ============================================================================
interface AnimatedCounterProps {
  value: number;
  duration?: number;
  decimals?: number;
  suffix?: string;
  prefix?: string;
}

const AnimatedCounter: React.FC<AnimatedCounterProps> = ({ 
  value, 
  duration = 1500, 
  decimals = 0, 
  suffix = '', 
  prefix = '' 
}) => {
  const [displayValue, setDisplayValue] = useState(0);
  const startTime = useRef<number | null>(null);
  const animationFrame = useRef<number | null>(null);

  useEffect(() => {
    startTime.current = null;
    
    const animate = (timestamp: number) => {
      if (!startTime.current) startTime.current = timestamp;
      const progress = Math.min((timestamp - startTime.current) / duration, 1);
      
      // Easing function for smooth deceleration
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setDisplayValue(easeOutQuart * value);
      
      if (progress < 1) {
        animationFrame.current = requestAnimationFrame(animate);
      }
    };
    
    animationFrame.current = requestAnimationFrame(animate);
    
    return () => {
      if (animationFrame.current) cancelAnimationFrame(animationFrame.current);
    };
  }, [value, duration]);

  const formattedValue = decimals > 0 
    ? displayValue.toFixed(decimals) 
    : Math.round(displayValue).toLocaleString();

  return <>{prefix}{formattedValue}{suffix}</>;
};

// ============================================================================
// SPARKLINE COMPONENT
// ============================================================================
interface SparklineProps {
  data: number[];
  color?: string;
  height?: number;
  showArea?: boolean;
}

const Sparkline: React.FC<SparklineProps> = ({ 
  data, 
  color = COLORS.primary, 
  height = 30,
  showArea = true 
}) => {
  if (!data || data.length < 2) return null;
  
  const width = 80;
  const padding = 2;
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  
  const points = data.map((value, i) => {
    const x = padding + (i / (data.length - 1)) * (width - padding * 2);
    const y = height - padding - ((value - min) / range) * (height - padding * 2);
    return `${x},${y}`;
  }).join(' ');

  const areaPoints = `${padding},${height - padding} ${points} ${width - padding},${height - padding}`;

  return (
    <svg width={width} height={height} className="sparkline">
      {showArea && (
        <polygon 
          points={areaPoints} 
          fill={`${color}20`}
        />
      )}
      <polyline 
        points={points} 
        fill="none" 
        stroke={color} 
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Dot at end */}
      <circle 
        cx={width - padding} 
        cy={height - padding - ((data[data.length - 1] - min) / range) * (height - padding * 2)}
        r="3"
        fill={color}
      />
    </svg>
  );
};

// ============================================================================
// UTILIZATION GAUGE COMPONENT
// ============================================================================
interface GaugeProps {
  value: number;
  size?: number;
  thickness?: number;
}

const UtilizationGauge: React.FC<GaugeProps> = ({ value, size = 90, thickness = 8 }) => {
  const [animatedValue, setAnimatedValue] = useState(0);
  
  useEffect(() => {
    const timer = setTimeout(() => setAnimatedValue(value), 100);
    return () => clearTimeout(timer);
  }, [value]);

  const radius = (size - thickness) / 2;
  const circumference = radius * Math.PI; // Semi-circle
  const progress = (animatedValue / 100) * circumference;
  
  // Color based on value
  const getColor = (val: number) => {
    if (val >= 70) return COLORS.success;
    if (val >= 50) return COLORS.warning;
    return COLORS.danger;
  };

  const color = getColor(animatedValue);

  return (
    <div className="gauge-container" style={{ width: size, height: size / 2 + 20 }}>
      <svg width={size} height={size / 2 + 10} viewBox={`0 0 ${size} ${size / 2 + 10}`}>
        {/* Background arc */}
        <path
          d={`M ${thickness / 2} ${size / 2} A ${radius} ${radius} 0 0 1 ${size - thickness / 2} ${size / 2}`}
          fill="none"
          stroke="rgba(0,0,0,0.1)"
          strokeWidth={thickness}
          strokeLinecap="round"
        />
        {/* Progress arc */}
        <path
          d={`M ${thickness / 2} ${size / 2} A ${radius} ${radius} 0 0 1 ${size - thickness / 2} ${size / 2}`}
          fill="none"
          stroke={color}
          strokeWidth={thickness}
          strokeLinecap="round"
          strokeDasharray={`${circumference}`}
          strokeDashoffset={circumference - progress}
          style={{ transition: 'stroke-dashoffset 1.5s ease-out, stroke 0.5s ease' }}
        />
        {/* Glow effect */}
        <path
          d={`M ${thickness / 2} ${size / 2} A ${radius} ${radius} 0 0 1 ${size - thickness / 2} ${size / 2}`}
          fill="none"
          stroke={color}
          strokeWidth={thickness + 4}
          strokeLinecap="round"
          strokeDasharray={`${circumference}`}
          strokeDashoffset={circumference - progress}
          opacity="0.3"
          filter="blur(4px)"
          style={{ transition: 'stroke-dashoffset 1.5s ease-out' }}
        />
      </svg>
      <div className="gauge-value">
        <AnimatedCounter value={value} decimals={1} suffix="%" />
      </div>
      <div className="gauge-label">Avg. Utilization</div>
    </div>
  );
};

// ============================================================================
// DELTA INDICATOR COMPONENT
// ============================================================================
interface DeltaIndicatorProps {
  current: number;
  previous: number | null;
  suffix?: string;
  invertColors?: boolean;
}

const DeltaIndicator: React.FC<DeltaIndicatorProps> = ({ 
  current, 
  previous, 
  suffix = '%',
  invertColors = false 
}) => {
  if (previous === null || previous === undefined) return null;
  
  const delta = current - previous;
  const percentChange = previous !== 0 ? ((delta / previous) * 100) : 0;
  const isPositive = delta > 0;
  const isNeutral = Math.abs(delta) < 0.1;
  
  let colorClass = 'neutral';
  if (!isNeutral) {
    colorClass = (isPositive && !invertColors) || (!isPositive && invertColors) ? 'positive' : 'negative';
  }

  return (
    <div className={`delta-indicator ${colorClass}`}>
      {!isNeutral && (
        <span className="delta-arrow">{isPositive ? '↑' : '↓'}</span>
      )}
      <span className="delta-value">
        {isPositive ? '+' : ''}{delta.toFixed(1)}{suffix}
      </span>
      <span className="delta-label">vs prev year</span>
    </div>
  );
};

// ============================================================================
// FULLSCREEN MODAL COMPONENT
// ============================================================================
interface FullscreenModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}

const FullscreenModal: React.FC<FullscreenModalProps> = ({ isOpen, onClose, title, children }) => {
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="rental-fullscreen-overlay" onClick={onClose}>
      <div className="rental-fullscreen-modal" onClick={e => e.stopPropagation()}>
        <div className="rental-fullscreen-header">
          <h2 className="rental-fullscreen-title">{title}</h2>
          <button className="rental-fullscreen-close" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="rental-fullscreen-content">
          {children}
        </div>
        <div className="rental-fullscreen-hint">Press ESC or click outside to close</div>
      </div>
    </div>
  );
};

// ============================================================================
// MAIN DASHBOARD COMPONENT
// ============================================================================
const RentalDashboard: React.FC = () => {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedFacility, setSelectedFacility] = useState<string | null>(null);
  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [fullscreenChart, setFullscreenChart] = useState<ChartType | null>(null);

  useEffect(() => {
    fetchAvailableYears();
  }, []);

  const fetchAvailableYears = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/rental-dashboard/available-years`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (!response.ok) throw new Error('Failed to fetch available years');
      const result = await response.json();
      if (result.success && result.years?.length > 0) {
        setAvailableYears(result.years);
        setSelectedYear(result.years[0]);
      }
    } catch (err: any) {
      setError(err.message);
      setLoading(false);
    }
  };

  const fetchDashboardData = useCallback(async (year: number) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/rental-dashboard/data?year=${year}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (!response.ok) {
        if (response.status === 403) throw new Error('Dashboard not available for your organization');
        throw new Error('Failed to fetch dashboard data');
      }
      const result = await response.json();
      if (result.success) {
        setData(result.data);
        const facilitiesWithData = Object.keys(result.data.utilizationByType || {});
        if (facilitiesWithData.length > 0) {
          setSelectedFacility(facilitiesWithData[0]);
        } else if (result.data.currentUtilization?.length > 0) {
          setSelectedFacility(result.data.currentUtilization[0].facilityName);
        }
      } else {
        throw new Error(result.error || 'Unknown error');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedYear) fetchDashboardData(selectedYear);
  }, [selectedYear, fetchDashboardData]);

  const handleYearChange = (year: number) => setSelectedYear(year);
  const handleChartDoubleClick = (chartType: ChartType) => setFullscreenChart(chartType);

  const formatMonth = (year: number, month: number) => {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${monthNames[month - 1]} ${String(year).slice(-2)}`;
  };

  const getMonthName = (month: number) => {
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 
                        'July', 'August', 'September', 'October', 'November', 'December'];
    return monthNames[month - 1] || '';
  };

  const getPerformanceClass = (utilization: number) => {
    if (utilization >= 70) return 'excellent';
    if (utilization >= 50) return 'good';
    if (utilization >= 30) return 'fair';
    return 'poor';
  };

  if (loading && !data) {
    return (
      <div className="rental-dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  if (error || !data) return null;

  // Build chart data
  const allPeriods = new Set<string>();
  Object.values(data.utilizationTrend).forEach(trends => {
    trends.forEach(t => allPeriods.add(`${t.year}-${String(t.month).padStart(2, '0')}`));
  });
  const sortedPeriods = Array.from(allPeriods).sort();
  const trendLabels = sortedPeriods.map(p => {
    const [year, month] = p.split('-');
    return formatMonth(parseInt(year), parseInt(month));
  });

  const trendDatasets = Object.entries(data.utilizationTrend).map(([region, trends], index) => {
    const dataPoints = sortedPeriods.map(period => {
      const [year, month] = period.split('-');
      const match = trends.find(t => t.year === parseInt(year) && t.month === parseInt(month));
      return match ? match.utilization : null;
    });
    return {
      label: region,
      data: dataPoints,
      borderColor: COLORS.regions[index % COLORS.regions.length],
      backgroundColor: COLORS.regions[index % COLORS.regions.length] + '20',
      tension: 0.4,
      fill: false,
      pointRadius: 4,
      pointHoverRadius: 6,
      borderWidth: 2.5,
      spanGaps: true
    };
  });

  const trendChartData = { labels: trendLabels, datasets: trendDatasets };

  const currentUtilChartData = {
    labels: data.currentUtilization.map(f => f.facilityName),
    datasets: [{
      label: 'Utilization %',
      data: data.currentUtilization.map(f => f.utilization),
      backgroundColor: data.currentUtilization.map(f => {
        const perf = getPerformanceClass(f.utilization);
        if (perf === 'excellent') return COLORS.success + 'CC';
        if (perf === 'good') return COLORS.primary + 'CC';
        if (perf === 'fair') return COLORS.warning + 'CC';
        return COLORS.danger + 'CC';
      }),
      borderColor: data.currentUtilization.map(f => {
        const perf = getPerformanceClass(f.utilization);
        if (perf === 'excellent') return COLORS.success;
        if (perf === 'good') return COLORS.primary;
        if (perf === 'fair') return COLORS.warning;
        return COLORS.danger;
      }),
      borderWidth: 1,
      borderRadius: 6
    }]
  };

  const selectedFacilityData = selectedFacility ? data.utilizationByType[selectedFacility] || [] : [];
  const equipmentTypeChartData = {
    labels: selectedFacilityData.map(e => e.equipmentType),
    datasets: [{
      label: 'Utilization %',
      data: selectedFacilityData.map(e => e.utilization),
      backgroundColor: selectedFacilityData.map(e => {
        const perf = getPerformanceClass(e.utilization);
        if (perf === 'excellent') return COLORS.success + 'CC';
        if (perf === 'good') return COLORS.primary + 'CC';
        if (perf === 'fair') return COLORS.warning + 'CC';
        return COLORS.danger + 'CC';
      }),
      borderColor: selectedFacilityData.map(e => {
        const perf = getPerformanceClass(e.utilization);
        if (perf === 'excellent') return COLORS.success;
        if (perf === 'good') return COLORS.primary;
        if (perf === 'fair') return COLORS.warning;
        return COLORS.danger;
      }),
      borderWidth: 1,
      borderRadius: 4
    }]
  };

  const fleetDoughnutData = {
    labels: data.fleetByRegion.map(r => r.region),
    datasets: [{
      data: data.fleetByRegion.map(r => r.unitCount),
      backgroundColor: data.fleetByRegion.map((_, i) => COLORS.regions[i % COLORS.regions.length] + 'CC'),
      borderColor: data.fleetByRegion.map((_, i) => COLORS.regions[i % COLORS.regions.length]),
      borderWidth: 2
    }]
  };

  // Fleet Status (Active vs Inactive) doughnut
  const fleetStatusData = {
    labels: ['Active', 'Inactive'],
    datasets: [{
      data: [data.fleetSummary.activeUnits, data.fleetSummary.inactiveUnits],
      backgroundColor: [COLORS.success + 'CC', COLORS.danger + 'CC'],
      borderColor: [COLORS.success, COLORS.danger],
      borderWidth: 2
    }]
  };

  const statusDoughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '65%',
    plugins: {
      legend: { 
        position: 'bottom' as const, 
        labels: { 
          usePointStyle: true, 
          padding: 8, 
          font: { size: 9, family: "'Inter', sans-serif" } 
        } 
      },
      tooltip: { 
        backgroundColor: 'rgba(15, 23, 42, 0.95)', 
        padding: 10, 
        cornerRadius: 8, 
        callbacks: { 
          label: (context: any) => {
            const total = data.fleetSummary.totalUnits;
            const percentage = ((context.parsed / total) * 100).toFixed(1);
            return `${context.label}: ${context.parsed.toLocaleString()} (${percentage}%)`;
          }
        } 
      }
    }
  };

  // Chart options
  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: { usePointStyle: true, padding: 15, font: { size: 11, family: "'Inter', sans-serif" } }
      },
      tooltip: {
        backgroundColor: 'rgba(15, 23, 42, 0.95)',
        padding: 12,
        titleFont: { size: 12, weight: 'bold' as const },
        bodyFont: { size: 11 },
        cornerRadius: 8,
        callbacks: {
          label: (context: any) => {
            const value = context.parsed.y;
            return `${context.dataset.label}: ${value !== null ? value.toFixed(1) : 'N/A'}%`;
          }
        }
      }
    },
    scales: {
      y: { beginAtZero: true, max: 100, ticks: { callback: (value: any) => `${value}%`, font: { size: 10 } }, grid: { color: 'rgba(0, 0, 0, 0.05)' } },
      x: { ticks: { font: { size: 10 }, maxRotation: 45, minRotation: 0 }, grid: { display: false } }
    }
  };

  const horizontalBarOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y' as const,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: 'rgba(15, 23, 42, 0.95)',
        padding: 12,
        titleFont: { size: 12, weight: 'bold' as const },
        bodyFont: { size: 12 },
        cornerRadius: 8,
        callbacks: {
          title: (items: any[]) => items[0]?.label || '',
          label: (context: any) => `Utilization: ${context.parsed.x?.toFixed(1) || 'N/A'}%`
        }
      }
    },
    scales: {
      x: { beginAtZero: true, max: 100, ticks: { callback: (value: any) => `${value}%`, font: { size: 10 } }, grid: { color: 'rgba(0, 0, 0, 0.05)' } },
      y: { ticks: { font: { size: 10 } }, grid: { display: false } }
    }
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '65%',
    plugins: {
      legend: { position: 'right' as const, labels: { usePointStyle: true, padding: 12, font: { size: 10, family: "'Inter', sans-serif" } } },
      tooltip: { backgroundColor: 'rgba(15, 23, 42, 0.95)', padding: 12, cornerRadius: 8, callbacks: { label: (context: any) => `${context.label}: ${context.parsed} units` } }
    }
  };

  const fullscreenLineOptions = { ...lineChartOptions, plugins: { ...lineChartOptions.plugins, legend: { ...lineChartOptions.plugins.legend, labels: { ...lineChartOptions.plugins.legend.labels, font: { size: 14, family: "'Inter', sans-serif" }, padding: 20 } } } };
  const fullscreenBarOptions = { ...horizontalBarOptions, scales: { ...horizontalBarOptions.scales, x: { ...horizontalBarOptions.scales.x, ticks: { ...horizontalBarOptions.scales.x.ticks, font: { size: 12 } } }, y: { ...horizontalBarOptions.scales.y, ticks: { font: { size: 12 } } } } };
  const fullscreenDoughnutOptions = { ...doughnutOptions, plugins: { ...doughnutOptions.plugins, legend: { ...doughnutOptions.plugins.legend, labels: { ...doughnutOptions.plugins.legend.labels, font: { size: 14, family: "'Inter', sans-serif" }, padding: 15 } } } };

  const facilitiesWithTypeData = Object.keys(data.utilizationByType);

  const getFullscreenTitle = (chartType: ChartType): string => {
    switch (chartType) {
      case 'trend': return `Utilization Trend by Region - ${data.selectedYear}`;
      case 'fleet': return 'Fleet Distribution by Region';
      case 'facility': return `${getMonthName(data.latestMonth)} ${data.selectedYear} Utilization by Facility`;
      case 'equipment': return `Utilization by Equipment Type - ${selectedFacility || ''}`;
      case 'status': return 'Fleet Status - Active vs Inactive Units';
      default: return '';
    }
  };

  const renderFullscreenChart = (chartType: ChartType) => {
    switch (chartType) {
      case 'trend': return <Line data={trendChartData} options={fullscreenLineOptions} />;
      case 'fleet': return <Doughnut data={fleetDoughnutData} options={fullscreenDoughnutOptions} />;
      case 'facility': return <Bar data={currentUtilChartData} options={fullscreenBarOptions} />;
      case 'equipment': return <Bar data={equipmentTypeChartData} options={fullscreenBarOptions} />;
      case 'status': return <Doughnut data={fleetStatusData} options={fullscreenDoughnutOptions} />;
      default: return null;
    }
  };

  return (
    <div className="rental-dashboard">
      <div className="rental-dashboard-header">
        <div className="rental-dashboard-title-row">
          <h2 className="rental-dashboard-title">
            <span className="title-icon">📊</span>
            Rental Utilization Dashboard
          </h2>
          {availableYears.length > 0 && (
            <div className="year-selector-container">
              <label className="year-selector-label">Year:</label>
              <select className="year-selector" value={selectedYear || ''} onChange={(e) => handleYearChange(parseInt(e.target.value))} disabled={loading}>
                {availableYears.map(year => <option key={year} value={year}>{year}</option>)}
              </select>
            </div>
          )}
        </div>
        <span className="rental-dashboard-subtitle">
          {data.selectedYear} data through {getMonthName(data.latestMonth)} • Double-click any chart to expand
        </span>
      </div>

      {loading && data && (
        <div className="rental-dashboard-loading-overlay">
          <div className="loading-spinner"></div>
        </div>
      )}

      {/* Charts Row 1: Trend + Doughnuts */}
      <div className="rental-charts-row-1">
        <div className="rental-chart-card glass clickable trend-card" onDoubleClick={() => handleChartDoubleClick('trend')} title="Double-click to expand">
          <h3 className="rental-chart-title">Utilization Trend - {data.selectedYear}</h3>
          <div className="rental-chart-container trend-chart">
            <Line data={trendChartData} options={lineChartOptions} />
          </div>
        </div>
        <div className="rental-chart-card glass clickable" onDoubleClick={() => handleChartDoubleClick('fleet')} title="Double-click to expand">
          <h3 className="rental-chart-title">Fleet by Region</h3>
          <div className="rental-chart-container doughnut">
            <Doughnut data={fleetDoughnutData} options={doughnutOptions} />
          </div>
        </div>
        <div className="rental-chart-card glass clickable" onDoubleClick={() => handleChartDoubleClick('status')} title="Double-click to expand">
          <h3 className="rental-chart-title">Fleet Status</h3>
          <div className="rental-chart-container doughnut">
            <Doughnut data={fleetStatusData} options={statusDoughnutOptions} />
          </div>
          <div className="status-summary">
            <span className="status-active">{((data.fleetSummary.activeUnits / data.fleetSummary.totalUnits) * 100).toFixed(0)}% Active</span>
          </div>
        </div>
      </div>

      {/* Charts Row 2: Bar Charts */}
      <div className="rental-charts-row-2">
        <div className="rental-chart-card glass clickable" onDoubleClick={() => handleChartDoubleClick('facility')} title="Double-click to expand">
          <h3 className="rental-chart-title">{getMonthName(data.latestMonth)} Utilization by Facility</h3>
          <div className="rental-chart-container">
            {data.currentUtilization.length > 0 ? (
              <Bar data={currentUtilChartData} options={horizontalBarOptions} />
            ) : (
              <div className="no-data-message"><span>No data</span></div>
            )}
          </div>
        </div>
        <div className="rental-chart-card glass clickable" onDoubleClick={() => handleChartDoubleClick('equipment')} title="Double-click to expand">
          <h3 className="rental-chart-title">
            By Equipment Type
            {facilitiesWithTypeData.length > 1 && (
              <select className="facility-selector" value={selectedFacility || ''} onChange={(e) => setSelectedFacility(e.target.value)} onClick={(e) => e.stopPropagation()}>
                {facilitiesWithTypeData.map(facilityName => <option key={facilityName} value={facilityName}>{facilityName}</option>)}
              </select>
            )}
          </h3>
          <div className="rental-chart-container">
            {selectedFacilityData.length > 0 ? (
              <Bar data={equipmentTypeChartData} options={horizontalBarOptions} />
            ) : (
              <div className="no-data-message"><span>No data</span></div>
            )}
          </div>
        </div>
      </div>

      <FullscreenModal isOpen={fullscreenChart !== null} onClose={() => setFullscreenChart(null)} title={fullscreenChart ? getFullscreenTitle(fullscreenChart) : ''}>
        {fullscreenChart && renderFullscreenChart(fullscreenChart)}
      </FullscreenModal>
    </div>
  );
};

export default RentalDashboard;
